import { cx } from "class-variance-authority";
import * as oddslib from "oddslib";
import type { Format } from "oddslib";
import BigNumber from "bignumber.js";

import * as styles from "./Odds.module.scss";

type OddsFormat =
  | "decimal"
  | "moneyline"
  | "points"
  | "american"
  | "fractional";
type FormatWithExtras = OddsFormat | Format;

export const formatOdds = (
  base: number,
  format: FormatWithExtras,
  prefix: string,
) => {
  if (format === "points") {
    const result = base > 100 ? base + " PTS" : "-";
    return `${prefix ? `${prefix} ` : ""}${result}`;
  }

  if (format === "american") {
    format = "moneyline";
  }

  let baseFormatted = "";

  try {
    baseFormatted = oddslib.from("decimal", base).to(format, {
      precision: format === "decimal" ? 2 : null,
    });
  } catch (err) {
    console.warn("Invalid odds base:", base, err);
    const fallback = base.toFixed(2);
    return `${prefix ? `${prefix} ` : ""}${fallback}`;
  }

  if (format === "decimal") {
    baseFormatted = Number(baseFormatted).toFixed(2);

    if (base > 1000) {
      const digits = Number(baseFormatted.split(".")[1]) > 0 ? 2 : 0;

      baseFormatted = new Intl.NumberFormat("en-AU", {
        style: "decimal",
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
      }).format(Number(baseFormatted));
    }
  }

  if (format === "moneyline") {
    const sign = Number(baseFormatted) > 0 ? "+" : "";
    baseFormatted = `${sign}${Number(baseFormatted).toFixed(0)}`;
  }

  return `${prefix ? `${prefix} ` : ""}${baseFormatted}`;
};

export type OddsProps = {
  base: number;
  format?: FormatWithExtras;
  className?: string;
  prefix?: string;
  zero?: string;
  settlementOdds?: number;
  isVoid?: boolean;
  removeVoidLabel?: boolean;
  showNewOdds?: boolean;
};

const Odds = ({
  base: baseProp,
  format = "decimal",
  className,
  prefix = "",
  zero = "-",
  isVoid,
  removeVoidLabel,
  showNewOdds = true,
  settlementOdds: settlementProp,
}: OddsProps) => {
  const base = new BigNumber(baseProp).dp(2, BigNumber.ROUND_FLOOR).toNumber();

  const settlementOdds =
    settlementProp !== undefined
      ? new BigNumber(settlementProp).dp(2, BigNumber.ROUND_FLOOR).toNumber()
      : undefined;

  let odds: string | number;
  let oldOdds: string | number | undefined;

  if (isVoid) {
    odds = removeVoidLabel ? "" : "VOID";
    oldOdds = formatOdds(base, format, "");
  } else if (
    settlementOdds !== undefined &&
    settlementOdds <= 1 &&
    settlementOdds !== base &&
    ["moneyline", "american", "fractional"].includes(format)
  ) {
    odds = `${prefix} ${formatOdds(base, format, "")} - ${((settlementOdds / base) * 100).toFixed(2)}%`;
  } else if (settlementOdds !== undefined && settlementOdds !== base) {
    odds = formatOdds(settlementOdds, format, "");
    oldOdds = base <= 0 ? zero : formatOdds(base, format, "");
  } else {
    odds = base <= 0 ? zero : formatOdds(base, format, prefix);
  }

  const status =
    settlementOdds !== undefined && !isVoid && base > 0
      ? settlementOdds < base
        ? "down"
        : settlementOdds > base
          ? "up"
          : undefined
      : undefined;

  return (
    <div
      title={String(odds)}
      className={cx(styles.odds, className)}
      data-status={status}
    >
      {oldOdds && (
        <span className={styles.oldOdds}>
          {prefix} <span>{oldOdds}</span>{" "}
        </span>
      )}
      {showNewOdds && <span>{odds}</span>}
    </div>
  );
};

export { Odds };
